// ===========================================
// components/LoginScreen.js
// ===========================================
import React, { useEffect, useRef } from 'react';
import { Book } from 'lucide-react';
import { useState } from 'react';
import config from '../utils/config';

const LoginScreen = ({ onLogin }) => {
  const telegramRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Handle Telegram redirect response
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('id')) {
      console.log('Telegram auth data from URL params');
      setLoading(true);
      
      // Extract Telegram auth data from URL parameters
      const authData = {};
      for (const [key, value] of urlParams) {
        authData[key] = value;
      }
      
      const authUrl = config.getTelegramAuthUrl();
      
      console.log('Auth URL being used:', authUrl);
      console.log('Configuration:', config.getAll());
      
      fetch(authUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(authData)
      })
        .then(res => res.json())
        .then(async (data) => {
          console.log('Backend response:', data);
          if (data.access_token) {
            try {
              await onLogin(data.access_token, data.user);
              // Don't set loading to false here - let the login function handle it
            } catch (loginError) {
              console.error('Login processing error:', loginError);
              setError('Failed to complete login process');
              setLoading(false);
            }
          } else {
            setError(data.error || 'Login failed');
            setLoading(false);
          }
        })
        .catch(err => {
          console.error('Network error:', err);
          setError('Network error');
          setLoading(false);
        });
      
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    // Use redirect mode for Telegram login (works with localhost)
    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.async = true;
    
    const botUsername = config.getTelegramBotUsername();
    
    console.log('Bot username being used:', botUsername);
    
    script.setAttribute('data-telegram-login', botUsername);
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-radius', '8');
    script.setAttribute('data-request-access', 'write');
    script.setAttribute('data-userpic', 'false');
    // Use redirect mode - redirects back to current page with auth data
    script.setAttribute('data-auth-url', window.location.origin + '/');
    
    console.log('Telegram widget configuration:', {
      botUsername: config.getTelegramBotUsername(),
      authUrl: config.getTelegramAuthUrl(),
      redirectUrl: window.location.origin + '/',
      allConfig: config.getAll()
    });
    
    const widgetContainer = telegramRef.current;
    if (widgetContainer) {
      widgetContainer.appendChild(script);
    }
    return () => {
      if (widgetContainer) {
        widgetContainer.innerHTML = '';
      }
    };
  }, [onLogin]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="w-full max-w-md p-8 mx-4 bg-white rounded-lg shadow-lg">
        <div className="text-center">
          <Book className="w-12 h-12 mx-auto mb-4 text-blue-600" />
          <h1 className="mb-2 text-2xl font-bold text-gray-900">E-Library System</h1>
          <p className="mb-6 text-gray-600">Access thousands of books and documents</p>
          
          {/* Telegram Login Widget */}
          <div ref={telegramRef} className="flex justify-center mb-4"></div>
          {loading && <p className="mt-2 text-sm text-blue-500">Logging in...</p>}
          {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
          
          {/* Fallback message for development */}
          <div className="p-3 mt-4 border border-yellow-200 rounded-lg bg-yellow-50">
            <p className="text-sm text-yellow-800">
              <strong>Development Note:</strong> If the Telegram login button doesn't work, 
              the domain needs to be configured in BotFather. See TELEGRAM_SETUP.md for details.
            </p>
          </div>
          <p className="mt-4 text-sm text-gray-500">
            Login requires admin approval before access is granted
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;